import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';

export interface ApiState<T> {
  data: T | null;
  error: string | null;
  /** True on first load and on every reload. */
  loading: boolean;
  reload: () => void;
}

/**
 * Loads `path` from the backend. `path === null` means "do not load yet".
 * Re-fetches when the path changes; `reload()` fetches again on demand.
 */
export function useApi<T>(path: string | null): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);
  const latest = useRef(0);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }
    const ticket = ++latest.current;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api<T>(path, { signal: controller.signal })
      .then((result) => {
        if (ticket !== latest.current) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (ticket !== latest.current || controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : 'Could not reach the server');
      })
      .finally(() => {
        if (ticket === latest.current) setLoading(false);
      });
    return () => controller.abort();
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

/** Message suitable for showing a person, from anything a request can throw. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.problem.detail || err.message;
  return err instanceof Error ? err.message : 'Something went wrong';
}
